import _ from 'lodash';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { CUSTOMER_PORTAL_PREVIEW } from 'hooks/use-customer-preview';
import { useQueryParamsFromWindow } from 'hooks/use-query-params';

interface CustomerPortalContextProps {
  projectId?: number | null;
  portalId?: number | null;
  isLoading: boolean;
  isPreviewAvailable: boolean;
  setProjectId: React.Dispatch<React.SetStateAction<number | null>>;
  setPortalId: React.Dispatch<React.SetStateAction<number | null>>;
  setPreview: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const customerPortalContext = createContext<CustomerPortalContextProps>({
  isLoading: true,
  isPreviewAvailable: false,
  setLoading: () => {},
  setPreview: () => {},
  setProjectId: () => {},
  setPortalId: () => {},
});

export const useCustomerPreviewForCustomerPortalV2 = () =>
  useContext(customerPortalContext);

export const CustomerPortalPreviewProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [isLoading, setLoading] = useState(true);
  const [isPreviewAvailable, setPreview] = useState(false);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [portalId, setPortalId] = useState<number | null>(null);

  const queryParams = useQueryParamsFromWindow();

  useEffect(() => {
    const preview = queryParams.get(CUSTOMER_PORTAL_PREVIEW);

    const projectIdStr = queryParams.get('project');
    const portalIdStr = queryParams.get('portal');

    if (preview !== null && projectIdStr !== null && isLoading && portalIdStr) {
      const _isPreview = preview === 'true';
      const _projectId = parseInt(projectIdStr, 10);
      const _portalId = parseInt(portalIdStr, 10);
      const isProjectIdValid = _.isFinite(_projectId);

      if (_isPreview && isProjectIdValid) {
        setProjectId(_projectId);
        setPortalId(_portalId);
        setPreview(true);
        setLoading(false);
      }
    } else if (isLoading !== false) {
      setLoading(false);
    }
  }, [isLoading, queryParams]);

  return (
    <customerPortalContext.Provider
      value={{
        isLoading,
        isPreviewAvailable,
        setLoading,
        setPreview,
        setProjectId,
        projectId,
        portalId,
        setPortalId,
      }}
    >
      {children}
    </customerPortalContext.Provider>
  );
};
